import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("failed"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* retry */ }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  return (
    <div className="relative z-10 pt-40 pb-32 max-w-2xl mx-auto px-6 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <div className="flex flex-col items-center gap-6 text-muted-foreground">
          <Loader2 size={48} className="animate-spin text-gold" />
          <p className="font-serif text-3xl text-foreground">Confermiamo il tuo incanto…</p>
          <p>Attendi qualche istante, stiamo verificando il pagamento.</p>
        </div>
      )}
      {status === "paid" && (
        <>
          <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
            <CheckCircle2 size={64} className="mx-auto text-gold mb-8" strokeWidth={1.2} />
          </motion.div>
          <h1 className="font-serif text-5xl md:text-6xl font-light mb-6" data-testid="success-title">Incanto confermato</h1>
          <p className="text-lg text-muted-foreground mb-10">Grazie! Il tuo ordine è stato pagato e preso in carico. Riceverai presto notizie.</p>
          <div className="flex gap-4 justify-center">
            <Button className="rounded-full bg-gold hover:bg-gold/90 text-white h-12 px-8" onClick={() => nav("/ordini")} data-testid="view-orders-btn">I miei ordini</Button>
            <Button variant="outline" className="rounded-full h-12 px-8" onClick={() => nav("/shop")}>Continua</Button>
          </div>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={64} className="mx-auto text-destructive mb-8" strokeWidth={1.2} />
          <h1 className="font-serif text-5xl font-light mb-6">Qualcosa è andato storto</h1>
          <p className="text-lg text-muted-foreground mb-10">Non siamo riusciti a confermare il pagamento. Se pensi sia un errore, controlla "I miei ordini" o riprova.</p>
          <Link to="/checkout"><Button className="rounded-full bg-gold hover:bg-gold/90 text-white h-12 px-8">Torna alla cassa</Button></Link>
        </>
      )}
    </div>
  );
}
